<template>
  <div :class="b({ hasFocus })">
    <!-- Desktop Search field -->
    <form
      v-if="!$viewport.isTablet"
      @submit.prevent="onSubmit"
    >
      <e-search
        v-model="searchTerm"
        :class="b('search')"
        :icon="hasFocus ? 'i-close' : 'i-search'"
        :label="$t('c-search-bar.searchLabel')"
        name="search"
        height="400"
        @focus="hasFocus = true"
      />
    </form>

    <!-- Mobile Toggle button -->
    <button
      v-else
      :class="b('mobile-trigger-button')"
      type="button"
      @click="onClickToggleMobileSearch"
    >
      <e-icon-sprite
        :alt="$t('c-header.searchAlt')"
        :class="b('mobile-trigger-icon')"
        icon="i-search"
      />
    </button>

    <!-- Flyout for autocomplete results -->
    <c-flyout
      ref="flyout"
      :open="hasFocus"
      :has-close-icon="false"
      :outside-click-excludes="[$el]"
      :spacing="0"
      :scrollable="false"
      @close="hasFocus = false"
    >
      <div :class="b('content-wrapper')">
        <!-- Mobile Search field -->
        <form @submit.prevent="onSubmit">
          <e-search
            v-if="!$viewport.isMd"
            v-model="searchTerm"
            ref="mobileSearchField"
            :class="b('search')"
            :icon="hasFocus ? 'i-close' : 'i-search'"
            :label="$t('c-search-bar.searchLabel')"
            :action-label="$t('c-search-bar.closeLabel')"
            :inactive-action-label="$t('c-search-bar.closeLabel')"
            name="search"
            height="400"
          />
        </form>

        <!-- Quick links -->
        <div
          v-if="!searchTerm || searchTerm.length < 3"
          :class="b('quick-links')"
        >
          <div
            v-if="searchStore.quickLinks.length"
            :class="b('content-title')"
          >
            {{ $t('c-search-bar.quickLinksTitle') }}
          </div>
          <ul :class="b('quick-link-list')">
            <li
              v-for="link in searchStore.quickLinks"
              :key="link.url"
              :class="b('quick-link-item')"
            >
              <a
                :href="link.url"
                :class="b('quick-link')"
                :target="link.target || '_self'"
              >
                {{ link.title }}
              </a>
            </li>
          </ul>
        </div>

        <c-tabs
          v-else-if="isDirty"
          :show-as-collapse-on-mobile="false"
          :class="b('tabs', { cartDisabled: !sessionStore.isCartEnabled })"
          list-width="short"
        >
          <c-tab-panel
            v-for="tab in suggestionTabs"
            :key="tab.id"
            :name="tab.name"
            :selected="activeTab === tab.id"
            :is-subtle="!searchStore.runningRequests.apiFetchSearchSuggestions && !tab.results.length"
            @change="activeTab = tab.id"
          >
            <ul
              v-if="tab.results.length"
              :class="b('suggestions-list')"
            >
              <li
                v-for="result in tab.results"
                :key="result.id"
                :class="b('suggestions-list-item')"
              >
                <c-search-suggestion :item="result" />
              </li>
              <li :class="b('suggestions-list-item', { showAll: true })">
                <a
                  :class="b('show-all-button')"
                  :href="tab.showAllLink"
                >
                  {{ $t('c-search-bar.showAllText') }}
                  <e-icon-sprite icon="i-chevron-2--right" />
                </a>
              </li>
            </ul>
            <i18n-t
              v-else-if="!searchStore.runningRequests.apiFetchSearchSuggestions"
              :class="b('no-results-text')"
              :keypath="tab.noResultsTranslation"
              scope="global"
            >
              <template #link>
                <a :href="sessionStore.urls.noSearchResultContactPage">
                  {{ $t('c-search-bar.noResultsLinkTextShopTab') }}
                </a>
              </template>
            </i18n-t>
          </c-tab-panel>
        </c-tabs>

        <!-- Loader -->
        <div
          v-if="searchStore.runningRequests.apiFetchSearchSuggestions"
          :class="b('loader')"
        >
          <e-progress />
        </div>
      </div>
    </c-flyout>
  </div>
</template>

<script>
  import useSearchStore from '@/stores/search';
  import useSessionStore from '@/stores/session';
  import cFlyout from '@/components/c-flyout.vue';
  import cSearchSuggestion from '@/components/c-search-suggestion.vue';
  import cTabPanel from '@/components/c-tab-panel.vue';
  import cTabs from '@/components/c-tabs.vue';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import eProgress from '@/elements/e-progress.vue';
  import eSearch from '@/elements/e-search.vue';
  import getShopSearchLink from '@/helpers/get-shop-search-link.js';

  /**
   * Search bar component handles autocomplete suggestions and search submits.
   */
  export default {
    name: 'c-search-bar',
    components: {
      cTabPanel,
      cTabs,
      eProgress,
      eIconSprite,
      cFlyout,
      cSearchSuggestion,
      eSearch,
    },
    // mixins: [],

    // props: {},

    emits: ['focus', 'blur'],
    setup() {
      return {
        searchStore: useSearchStore(),
        sessionStore: useSessionStore(),
      };
    },

    data() {
      return {
        /**
         * @type {Boolean} Shows if the search input has the focus.
         */
        hasFocus: false,

        /**
         * @type {String} Holds the current search-term of the input field.
         */
        searchTerm: '',

        /**
         * @type {Number|null} A Number, representing the ID value of the timer that is set.
         */
        debouncedTimeout: null,

        /**
         * @type {Number} The debounce time in milliseconds.
         */
        debounceValue: 300,

        /**
         * @type {String|null} Holds the currently active tab.
         */
        activeTabInternal: null,

        /**
         * @type {Boolean} Defines if search results have been changed.
         */
        isDirty: false,
      };
    },

    computed: {
      /**
       * Returns a definition Array for result tabs.
       * @returns {Array.<Object>}
       */
      suggestionTabs() {
        const { searchSuggestionsCommerce, searchSuggestionsPimcore } = this.searchStore;

        const tabs = [
          {
            id: 'shop',
            name: this.$t('c-search-bar.tabShop'),
            results: searchSuggestionsCommerce,
            onShowAll: this.onClickShowAllShop,
            showAllLink: this.showAllLinkShop,
            noResultsTranslation: 'c-search-bar.noResultsTextShopTab',
          },
          {
            id: 'website',
            name: this.$t('c-search-bar.tabWebsite'),
            results: searchSuggestionsPimcore,
            onShowAll: this.onClickShowAllWebsite,
            showAllLink: this.showAllLinkWebsite,
            noResultsTranslation: 'c-search-bar.noResultsTextWebsiteTab',
          },
        ];

        return this.sessionStore.hasInvertedSearchSuggestionTabs ? tabs.reverse() : tabs;
      },

      /**
       * Gets/sets the active search tab.
       */
      activeTab: {
        get() {
          if (!this.sessionStore.isCartEnabled) {
            return 'website';
          }

          if (this.activeTabInternal) {
            return this.activeTabInternal;
          }

          if (this.sessionStore.hasInvertedSearchSuggestionTabs) {
            return !this.searchStore.searchSuggestionsPimcore.length &&
              this.searchStore.searchSuggestionsCommerce.length
              ? 'shop'
              : 'website';
          }

          return this.searchStore.searchSuggestionsPimcore.length && !this.searchStore.searchSuggestionsCommerce.length
            ? 'website'
            : 'shop';
        },

        set(activeTab) {
          this.activeTabInternal = activeTab;
        },
      },

      /**
       * Return shop-url including the search term.
       *
       * @returns {String} Shop-query-url
       */
      showAllLinkShop() {
        return getShopSearchLink(this.searchTerm);
      },

      /**
       * Return website-url including the search term.
       *
       * @returns {String} Website-query-url
       */
      showAllLinkWebsite() {
        return `${this.sessionStore.urls.searchResultPage}?query=${this.searchTerm}`;
      },
    },

    watch: {
      /**
       * Observes the search term and fetches the suggestions with the defined debounce.
       *
       * @param {String} term - The search term.
       */
      searchTerm(term) {
        if (term && term.length >= 3) {
          if (this.debounceValue > 0) {
            clearTimeout(this.debouncedTimeout);

            this.debouncedTimeout = setTimeout(
              () =>
                this.searchStore.apiFetchSearchSuggestions({ term }).finally(() => {
                  this.debouncedTimeout = null;
                  this.isDirty = true;
                }),
              this.debounceValue,
            );
          } else {
            this.searchStore.apiFetchSearchSuggestions({ term }).finally(() => {
              this.isDirty = true;
            });
          }
        }
      },

      /**
       * Observes the focus state which defines if the flyout is open or closed.
       * Resets the search suggestions when the flyout gets closed.
       *
       * @param {Boolean} state - The focus state.
       */
      hasFocus(state) {
        if (state) {
          this.$emit('focus');
          setTimeout(() => {
            document.body.classList.add('disable-bodyscroll');
          });
        } else {
          document.body.classList.remove('disable-bodyscroll');
          this.isDirty = false;
          this.searchStore.searchSuggestionsPimcore = [];
          this.searchStore.searchSuggestionsCommerce = [];
          this.searchTerm = '';
          this.$emit('blur');
        }
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Input field submit event handler.
       */
      onSubmit() {
        if (this.searchTerm) {
          this.submitSearch();
        } else {
          this.hasFocus = false;
        }
      },

      /**
       * Submits the search for the website or the shop.
       */
      submitSearch() {
        if (this.activeTab === 'website') {
          this.searchStore.submitSearchWebsite({ query: this.searchTerm });

          return;
        }

        if (this.sessionStore.isCartEnabled && this.searchStore.searchSuggestionsCommerce?.length) {
          this.searchStore.submitSearchShop({ query: this.searchTerm });

          return;
        }

        this.searchStore.submitSearchWebsite({ query: this.searchTerm });
      },

      /**
       * Handler for show all button in shop tab.
       */
      onClickShowAllShop() {
        this.searchStore.submitSearchShop({ query: this.searchTerm });
      },

      /**
       * Handler for show all button in website tab.
       */
      onClickShowAllWebsite() {
        this.searchStore.submitSearchWebsite({ query: this.searchTerm });
      },

      /**
       * Open mobile search flyout.
       */
      openMobileSearch() {
        this.hasFocus = true;

        // Timeout is needed to be sure we can focus the input field where it's possible (for e.g. iOS is not possible).
        setTimeout(() => {
          const { input } = this.$refs.mobileSearchField?.$refs || {};

          if (input) {
            input.focus();
          }
        });
      },

      /**
       * Event handler for the mobile search toggle.
       */
      onClickToggleMobileSearch() {
        if (this.hasFocus) {
          this.hasFocus = false;
        } else {
          this.openMobileSearch();
        }
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:math';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  .c-search-bar {
    &__mobile-trigger-button {
      @extend %button-reset;

      display: flex;
      align-items: center;
    }

    &__mobile-trigger-icon {
      color: variables.$color-primary--1;

      .e-icon-sprite {
        width: functions.rem(24px);
        height: functions.rem(24px);
      }
    }

    &__content-wrapper {
      height: 100%;
      padding: functions.rem(variables.$spacing--20);

      @include mixins.media(sm) {
        padding: functions.rem(38px variables.$spacing--80);
      }

      .e-search {
        margin-bottom: functions.rem(variables.$spacing--30);
      }

      .c-tabs {
        display: flex;
        flex-direction: column;
        height: calc(100% - 80px);
        padding: 0;

        @include mixins.media(md) {
          height: 100%;
        }
      }

      .c-tabs__head {
        flex: 0 0 auto;
      }

      .c-tab-panel {
        position: relative;
        flex: 1 1 auto;
      }
    }

    &__quick-link-list {
      @extend %list-reset;
    }

    &__content-title {
      @extend %heading-h3;
      @include mixins.font(variables.$font-size--16, 24px, variables.$font-weight--regular);

      margin: functions.rem(0 0 variables.$spacing--25);
      color: variables.$color-grayscale--100;

      @include mixins.media(md) {
        margin: functions.rem(0 0 variables.$spacing--30);
      }
    }

    &__quick-link-item:not(:last-of-type) {
      margin-bottom: functions.rem(variables.$spacing--30);
    }

    &__quick-link {
      @include mixins.font(variables.$font-size--18, 24px, variables.$font-weight--semi-bold);

      display: block;
      color: variables.$color-primary--1;

      &:hover,
      &:focus {
        color: variables.$color-primary--2;
      }
    }

    &__loader {
      position: absolute;
      top: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      width: 100%;
      height: 100%;
      padding-top: functions.rem(variables.$spacing--100);
      background-color: rgba(variables.$color-grayscale--1000, 0.7);

      @include mixins.media(md) {
        align-items: center;
        padding-top: 0;
      }
    }

    &__suggestions-list {
      @extend %list-reset;

      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      padding: functions.rem(variables.$spacing--30 0);
      overflow-y: auto;
    }

    &__suggestions-list-item {
      padding-right: functions.rem(variables.$spacing--10);
    }

    &__suggestions-list-item:not(:last-of-type) {
      margin-bottom: functions.rem(variables.$spacing--30);
    }

    &__show-all-button {
      @extend %button-reset;
      @include mixins.font(variables.$font-size--16, 24px, variables.$font-weight--semi-bold);

      display: flex;
      align-items: center;
      margin-top: functions.rem(variables.$spacing--50);
      margin-left: functions.rem(variables.$spacing--80);
      color: variables.$color-primary--2;

      .e-icon-sprite {
        @include mixins.showMoreIcon();

        margin-top: 0.2em;
      }

      .e-search__field {
        @include mixins.font(variables.$font-size--18);
      }
    }

    &__no-results-text {
      @include mixins.font(variables.$font-size--18, variables.$line-height--32);

      display: block;
      padding-top: functions.rem(variables.$spacing--60);
      color: variables.$color-primary--1;

      @include mixins.media(md) {
        max-width: math.percentage(math.div(3, 4));
      }
    }

    &__tabs--cart-disabled .c-tabs__head {
      display: none;
    }

    &--has-focus {
      .e-search .e-icon-sprite {
        width: functions.rem(20px);
        height: functions.rem(20px);
      }
    }
  }
</style>
